import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { useSnackbarController } from "@rebasepro/app";
import { useSidePanel } from "@rebasepro/cms";
import {
    Alert,
    Button,
    Card,
    CircularProgressCenter,
    cls,
    defaultBorderMixin,
    ExternalLinkIcon,
    FilterChip,
    IconButton,
    iconSize,
    LucideIconByName,
    Menu,
    MenuItem,
    PenLineIcon,
    RefreshCwIcon,
    SearchBar,
    Select,
    SelectItem,
    Tooltip,
    Typography
} from "@rebasepro/ui";
import { inboxLenses, lensWhere, type InboxLens } from "rebase-leadgen-config";
import { client } from "../lib/rebaseClient";
import { EmailComposer, type ComposerTarget } from "../inbox/EmailComposer";
import { EmailThreadView } from "../inbox/EmailThreadView";
import { ThreadLinkPicker } from "../inbox/ThreadLinkPicker";
import {
    daysWaiting,
    describeError,
    followUpLabel,
    FREEMAIL,
    gmailThreadUrl,
    inboxApi,
    isFollowUpDue,
    filedLabel,
    isPersonThread,
    sinceLabel,
    threadPerson,
    threadTitle,
    threadWith,
    useAutoSync,
    useMailboxes,
    type Company,
    type MailboxSummary,
    type Message,
    type Person,
    type Thread
} from "../inbox/shared";

/**
 * The inbox — every mailbox, one list.
 *
 * Conversations on the left, the conversation on the right, a box to answer in
 * under it. Three panes is what every mail client has settled on because it is
 * the shape of the task; below a laptop's width it becomes one pane at a time.
 *
 * ── What is counted where ────────────────────────────────────────────────────
 * The tabs are `config/inbox-lenses.ts`, the same definitions the morning brief
 * counts, and their numbers are counted on the SERVER over the whole table — not
 * over whichever page the browser loaded. A tab that says 12 holds 12.
 *
 * ── Things this screen deliberately does not do ──────────────────────────────
 *   · Opening the first conversation on its own. Selecting marks a
 *     conversation read, and a screen that marks mail read because it happened
 *     to be at the top of a tab is lying about what was read.
 *   · Change anything in the mailbox itself. Archive, "no reply needed" and a
 *     follow-up date are this CRM's; the mailbox stays as its owner left it.
 */

const LIST_LIMIT = 150;

/**
 * The tabs that are always on screen. The rest — not linked, filtered,
 * archived — are a menu away: they are places to look now and then, not
 * piles to work through, and seven chips wrap onto two lines in a list this
 * narrow.
 */
const MAIN_LENSES: InboxLens[] = ["waiting", "unread", "today", "all"];

const ALL_MAILBOXES = "__all__";

type LinkedNames = Map<string, string>;

/** A mailbox's colour, as a dot. Listed whole so the classes survive the build. */
const MAILBOX_DOT: Record<string, string> = {
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    orange: "bg-orange-500",
    teal: "bg-teal-500",
    pink: "bg-pink-500"
};

function Dot({ className }: { className: string }) {
    return <span aria-hidden className={cls("inline-block w-1.5 h-1.5 rounded-full shrink-0", className)} />;
}

function MailboxName({ mailbox }: { mailbox: MailboxSummary }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <Dot className={MAILBOX_DOT[mailbox.color ?? ""] ?? MAILBOX_DOT.blue} />
            {mailbox.name}
        </span>
    );
}

export function InboxView() {
    const [params, setParams] = useSearchParams();
    const snackbar = useSnackbarController();
    const sidePanel = useSidePanel();
    const { mailboxes, loaded: mailboxesLoaded, reload: reloadMailboxes } = useMailboxes();

    const lensParam = params.get("lens") as InboxLens | null;
    const [lens, setLens] = useState<InboxLens>(lensParam ?? "waiting");
    const lensChosen = useRef(Boolean(lensParam));
    const [mailboxFilter, setMailboxFilter] = useState<string | null>(params.get("mailbox"));
    const [counts, setCounts] = useState<Partial<Record<InboxLens, number>>>({});
    const [threads, setThreads] = useState<Thread[] | null>(null);
    const [search, setSearch] = useState("");
    const [names, setNames] = useState<LinkedNames>(new Map());
    const [selectedId, setSelectedId] = useState<string | null>(params.get("thread"));
    const [selected, setSelected] = useState<Thread | null>(null);
    const [messages, setMessages] = useState<Message[]>([]);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [composeNew, setComposeNew] = useState<ComposerTarget | null>(
        params.get("to") !== null || params.get("compose") !== null
            ? { mode: "new", to: params.get("to") ? [params.get("to")!] : [], subject: params.get("subject") ?? "" }
            : null
    );
    const [detailOpen, setDetailOpen] = useState(Boolean(params.get("thread") || params.get("to") || params.get("compose")));
    const [replying, setReplying] = useState(false);
    const [linking, setLinking] = useState(false);
    const [silencing, setSilencing] = useState(false);
    const [working, setWorking] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState<string | undefined>();

    const lenses = useMemo(() => inboxLenses(), []);
    const lensDef = lenses.find(l => l.id === lens) ?? lenses[0];
    const mailboxById = useMemo(() => new Map(mailboxes.map(m => [m.id, m])), [mailboxes]);
    const multipleMailboxes = mailboxes.length > 1;

    // Keep the address bar saying what is on screen, so a view can be shared,
    // reloaded, or linked to from the brief. The composer's own parameters are
    // read once and dropped.
    useEffect(() => {
        const next = new URLSearchParams();
        next.set("lens", lens);
        if (mailboxFilter) next.set("mailbox", mailboxFilter);
        if (selectedId) next.set("thread", selectedId);
        setParams(next, { replace: true });
    }, [lens, mailboxFilter, selectedId, setParams]);

    // ── Loading ──────────────────────────────────────────────────────────

    const loadCounts = useCallback(async () => {
        const entries = await Promise.all(lenses.map(async l => {
            try {
                const { meta } = await client.data.collection<Thread>("email_threads").find({
                    where: lensWhere(l.id, mailboxFilter) as never,
                    limit: 1
                });
                return [l.id, meta?.total ?? 0] as const;
            } catch {
                return [l.id, undefined] as const;
            }
        }));
        const next = Object.fromEntries(entries) as Partial<Record<InboxLens, number>>;
        setCounts(next);
        // The first tab worth opening, decided once: what is due today, then
        // what is unread, then what is waiting — never an empty tab when a full
        // one exists.
        if (!lensChosen.current) {
            lensChosen.current = true;
            const first = (["today", "unread", "waiting"] as InboxLens[]).find(id => (next[id] ?? 0) > 0) ?? "all";
            setLens(first);
        }
    }, [lenses, mailboxFilter]);

    const loadThreads = useCallback(async () => {
        setError(undefined);
        try {
            const base = lensWhere(lens, mailboxFilter) as Record<string, unknown>;
            const orderBy: ["follow_up_at", "asc"] | ["last_message_at", "desc"] = lens === "today" ? ["follow_up_at", "asc"] : ["last_message_at", "desc"];
            const q = search.trim().toLowerCase();
            const queries: Record<string, unknown>[] = !q
                ? [base]
                : q.includes("@")
                    ? [{ ...base, participants: ["array-contains", q] }]
                    : [{ ...base, subject: ["ilike", `%${q}%`] }, { ...base, counterpart_name: ["ilike", `%${q}%`] }];
            const found = new Map<string, Thread>();
            for (const where of queries) {
                const { data } = await client.data.collection<Thread>("email_threads").find({ where: where as never, orderBy, limit: LIST_LIMIT });
                for (const thread of data ?? []) found.set(thread.id, thread);
            }
            const list = [...found.values()].sort((a, b) => lens === "today"
                ? String(a.follow_up_at ?? "").localeCompare(String(b.follow_up_at ?? ""))
                : String(b.last_message_at ?? "").localeCompare(String(a.last_message_at ?? "")));
            setThreads(list);
        } catch (err) {
            setError(describeError(err));
            setThreads([]);
        }
    }, [lens, mailboxFilter, search]);

    const reload = useCallback(async () => {
        await Promise.all([loadThreads(), loadCounts()]);
    }, [loadThreads, loadCounts]);

    useEffect(() => { void loadCounts(); }, [loadCounts]);
    useEffect(() => { setThreads(null); void loadThreads(); }, [loadThreads]);

    // The names of whoever the listed conversations are linked to.
    useEffect(() => {
        const rows = [...(threads ?? []), ...(selected ? [selected] : [])];
        const people = [...new Set(rows.map(t => t.prospectId).filter((v): v is string => Boolean(v)))].filter(id => !names.has(id));
        const companies = [...new Set(rows.map(t => t.companyId).filter((v): v is string => Boolean(v)))].filter(id => !names.has(id));
        if (!people.length && !companies.length) return;
        let live = true;
        (async () => {
            const next = new Map(names);
            if (people.length) {
                const { data } = await client.data.collection<Person>("prospects").find({ where: { id: ["in", people] } as never, limit: people.length });
                for (const p of data ?? []) next.set(p.id, p.display_name || p.handle || p.email || "a person");
            }
            if (companies.length) {
                const { data } = await client.data.collection<Company>("companies").find({ where: { id: ["in", companies] } as never, limit: companies.length });
                for (const c of data ?? []) next.set(c.id, c.name || c.domain || "a company");
            }
            if (live) setNames(next);
        })().catch(() => { /* names are a nicety; the addresses still show */ });
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [threads, selected]);

    // The selected conversation and its messages — loaded on their own, so a
    // conversation opened from a link shows even when it is in another tab.
    const wanted = useRef<string | null>(null);
    const loadSelected = useCallback(async (id: string) => {
        wanted.current = id;
        setLoadingMessages(true);
        try {
            const [{ data: rows }, { data: msgs }] = await Promise.all([
                client.data.collection<Thread>("email_threads").find({ where: { id: ["==", id] } as never, limit: 1 }),
                client.data.collection<Message>("email_messages").find({ where: { threadId: ["==", id] } as never, orderBy: ["sent_at", "asc"], limit: 200 })
            ]);
            if (wanted.current !== id) return;
            setSelected((rows ?? [])[0] ?? null);
            setMessages(msgs ?? []);
        } catch (err) {
            if (wanted.current === id) setError(describeError(err));
        } finally {
            if (wanted.current === id) setLoadingMessages(false);
        }
    }, []);

    useEffect(() => {
        setReplying(false);
        setLinking(false);
        setSilencing(false);
        setMessages([]);
        if (!selectedId) { setSelected(null); return; }
        setSelected(threads?.find(t => t.id === selectedId) ?? null);
        void loadSelected(selectedId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedId, loadSelected]);

    const selectThread = useCallback((thread: Thread) => {
        setComposeNew(null);
        setSelectedId(thread.id);
        setDetailOpen(true);
        // Opened by a person, so it has been read. Optimistic, and put back if
        // the write fails — a badge that clears and returns is honest; one that
        // clears and lies is not.
        if (thread.unread) {
            setThreads(rows => rows?.map(t => (t.id === thread.id ? { ...t, unread: false } : t)) ?? rows);
            client.data.collection("email_threads").update(thread.id, { unread: false })
                .then(() => void loadCounts())
                .catch(() => setThreads(rows => rows?.map(t => (t.id === thread.id ? { ...t, unread: true } : t)) ?? rows));
        }
    }, [loadCounts]);

    // ── Reading the mailboxes ────────────────────────────────────────────

    const syncNow = async () => {
        setSyncing(true);
        try {
            const result = await inboxApi.sync({ force: true, mailbox: mailboxFilter });
            const kept = Number(result?.kept ?? 0);
            snackbar.open({ type: result?.success === false ? "warning" : "success", message: kept > 0 ? `${kept} new message(s)` : result?.success === false ? "Some mailboxes could not be read — see the banner." : "Nothing new." });
            await Promise.all([reload(), reloadMailboxes()]);
            if (selectedId) await loadSelected(selectedId);
        } catch (err) {
            setError(describeError(err));
        } finally {
            setSyncing(false);
        }
    };

    useAutoSync(async () => {
        try {
            const result = await inboxApi.sync({ mailbox: mailboxFilter });
            if (!result?.skipped && Number(result?.kept ?? 0) > 0) {
                await reload();
                if (selectedId) await loadSelected(selectedId);
            }
        } catch {
            // A failed background poll is not worth a banner; the button says so
            // out loud when somebody asks directly.
        }
    }, mailboxes.some(m => m.syncEnabled));

    // ── Acting on a conversation ─────────────────────────────────────────

    const patchThread = async (thread: Thread, patch: Partial<Thread>, message: string) => {
        setWorking(true);
        try {
            await client.data.collection("email_threads").update(thread.id, patch as Record<string, unknown>);
            setSelected(current => (current && current.id === thread.id ? { ...current, ...patch } : current));
            snackbar.open({ type: "success", message });
            await reload();
        } catch (err) {
            snackbar.open({ type: "error", message: describeError(err) });
        } finally {
            setWorking(false);
        }
    };

    const remind = (thread: Thread, days: number | null) => {
        if (days === null) return patchThread(thread, { follow_up_at: null }, "Reminder removed");
        const when = new Date();
        when.setDate(when.getDate() + days);
        when.setHours(9, 0, 0, 0);
        return patchThread(thread, { follow_up_at: when.toISOString() }, days === 1 ? "It will come back tomorrow" : `It will come back in ${days} days`);
    };

    const setKind = async (thread: Thread, kind: "person" | "automated", scope?: "address" | "domain") => {
        setWorking(true);
        try {
            const result = await inboxApi.kind(thread.id, { kind, scope });
            snackbar.open({
                type: "success",
                message: kind === "automated"
                    ? `Silenced ${result.rule ?? "this sender"} — this and their next messages go to Filtered`
                    : `${result.rule ?? "This sender"} is back in the inbox and will stay there`
            });
            setSilencing(false);
            await reload();
            await loadSelected(thread.id);
        } catch (err) {
            snackbar.open({ type: "error", message: describeError(err) });
        } finally {
            setWorking(false);
        }
    };

    // ── The banner: which mailboxes are not being read, and why ──────────

    const failing = mailboxes.filter(m => m.syncEnabled && (m.status === "error" || m.status === "needs_password" || !m.canRead));
    const shownMailboxes = mailboxFilter ? mailboxes.filter(m => m.id === mailboxFilter) : mailboxes;
    const lastRead = shownMailboxes.map(m => m.lastSyncedAt).filter(Boolean).sort()[0];

    const banner = !mailboxesLoaded ? null : mailboxes.length === 0 ? (
        <Alert
            color="info"
            size="small"
            action={<Button size="small" variant="text" onClick={() => sidePanel.open({ path: "mailboxes" })}>Add a mailbox</Button>}
        >
            No mailbox yet. Add one — its address and an app password — and its mail is read into this list.
        </Alert>
    ) : failing.length > 0 ? (
        <Alert color="warning" size="small">
            {failing.map(m => (
                <span key={m.id} className="block">
                    <button type="button" className="underline" onClick={() => sidePanel.open({ path: "mailboxes", entityId: m.id })}>{m.name}</button>
                    {" "}is not being read — {m.lastError || m.readProblems.join(" ") || "open it to see why"}
                </span>
            ))}
        </Alert>
    ) : null;

    const chooseLens = (id: InboxLens) => { lensChosen.current = true; setLens(id); };
    const lensCount = (id: InboxLens) => (counts[id] ? ` · ${counts[id]}` : "");
    const shownLenses = lenses.filter(l => MAIN_LENSES.includes(l.id) || l.id === lens);
    const moreLenses = lenses.filter(l => !MAIN_LENSES.includes(l.id));

    // ── Layout ───────────────────────────────────────────────────────────

    const selectedMailbox = selected?.mailboxId ? mailboxById.get(selected.mailboxId) : undefined;
    const gmailUrl = selected && selectedMailbox?.provider === "gmail" ? gmailThreadUrl(selected.gmail_thread_id, selectedMailbox.address) : null;
    const linkedName = (thread: Thread) => (thread.prospectId && names.get(thread.prospectId)) || (thread.companyId && names.get(thread.companyId)) || null;

    return (
        <div className="flex h-full w-full overflow-hidden">
            {/* ── The list ── */}
            <div className={cls("w-full lg:w-[25rem] shrink-0 border-r flex flex-col h-full", defaultBorderMixin, detailOpen && "max-lg:hidden")}>
                <div className="px-3 pt-3 pb-2 flex flex-col gap-2">
                    <div className="flex items-center gap-1">
                        <Typography variant="subtitle1" component="h1" className="grow pl-1">Inbox</Typography>
                        {multipleMailboxes && (
                            <Select
                                size="smallest"
                                aria-label="Mailbox"
                                value={mailboxFilter ?? ALL_MAILBOXES}
                                onValueChange={value => setMailboxFilter(value === ALL_MAILBOXES ? null : value)}
                                renderValue={value => (value === ALL_MAILBOXES ? "All inboxes" : mailboxById.get(value)?.name ?? "Mailbox")}
                            >
                                <SelectItem value={ALL_MAILBOXES}>All inboxes</SelectItem>
                                {mailboxes.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                            </Select>
                        )}
                        <Tooltip title="Write a new message">
                            <IconButton size="small" aria-label="Write a new message" onClick={() => {
                                setSelectedId(null);
                                setComposeNew({ mode: "new", to: [], subject: "", mailboxId: mailboxFilter });
                                setDetailOpen(true);
                            }}>
                                <PenLineIcon size={iconSize.smallest} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={lastRead ? `Check for new mail — last read ${sinceLabel(lastRead)}` : "Check for new mail"}>
                            <IconButton size="small" aria-label="Check for new mail" disabled={syncing || mailboxes.length === 0} onClick={() => void syncNow()}>
                                <RefreshCwIcon size={iconSize.smallest} className={cls(syncing && "animate-spin")} />
                            </IconButton>
                        </Tooltip>
                    </div>

                    {banner}

                    <SearchBar size="small" placeholder="Search by subject, name or address" onTextSearch={text => setSearch(text ?? "")} />

                    <div className="flex items-center gap-0.5 flex-wrap">
                        {shownLenses.map(l => (
                            <Tooltip key={l.id} title={l.hint}>
                                <FilterChip size="small" active={lens === l.id} onClick={() => chooseLens(l.id)}>
                                    {l.label}{lensCount(l.id)}
                                </FilterChip>
                            </Tooltip>
                        ))}
                        <Menu
                            align="end"
                            trigger={
                                <IconButton size="small" aria-label="More tabs">
                                    <LucideIconByName name="Ellipsis" size={iconSize.smallest} />
                                </IconButton>
                            }
                        >
                            {moreLenses.map(l => (
                                <MenuItem key={l.id} dense onClick={() => chooseLens(l.id)}>
                                    {l.label}{lensCount(l.id)}
                                </MenuItem>
                            ))}
                        </Menu>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-2 pb-4 flex flex-col gap-0.5">
                    {error && <Alert color="error" size="small">{error}</Alert>}
                    {threads === null && <CircularProgressCenter />}
                    {threads?.length === 0 && (
                        <div className="p-4 flex flex-col gap-1">
                            <Typography variant="body2" color="secondary">
                                {search ? "Nothing matches that search." : lensDef.empty}
                            </Typography>
                            {!search && <Typography variant="caption" color="secondary">{lensDef.hint}</Typography>}
                        </div>
                    )}
                    {threads?.map(thread => (
                        <ThreadRow
                            key={thread.id}
                            thread={thread}
                            selected={thread.id === selectedId}
                            who={threadPerson(thread, linkedName(thread))}
                            mailbox={multipleMailboxes && !mailboxFilter && thread.mailboxId ? mailboxById.get(thread.mailboxId) : undefined}
                            onSelect={selectThread}
                        />
                    ))}
                </div>
            </div>

            {/* ── The conversation ── */}
            <div className={cls("flex-1 min-w-0 overflow-y-auto", !detailOpen && "max-lg:hidden")}>
                <div className="lg:hidden sticky top-0 z-10 px-2 py-1 bg-surface-sheet">
                    <Button size="small" variant="text" onClick={() => setDetailOpen(false)}>← Conversations</Button>
                </div>

                {composeNew && (
                    <div className="p-6 max-w-3xl flex flex-col gap-5">
                        <Typography variant="h5">New message</Typography>
                        <EmailComposer
                            target={composeNew}
                            mailboxes={mailboxes}
                            onSent={async ({ threadId }) => {
                                setComposeNew(null);
                                snackbar.open({ type: "success", message: "Sent" });
                                await reload();
                                if (threadId) setSelectedId(threadId);
                            }}
                            onCancel={() => { setComposeNew(null); setDetailOpen(false); }}
                        />
                    </div>
                )}

                {!composeNew && !selectedId && (
                    <div className="h-full flex items-center justify-center p-8">
                        <Typography variant="body2" color="secondary">Pick a conversation, or write a new message.</Typography>
                    </div>
                )}

                {!composeNew && selectedId && !selected && loadingMessages && <CircularProgressCenter />}

                {!composeNew && selected && (
                    <div className="p-6 max-w-3xl flex flex-col gap-4">
                        {/* Header: what it is and who it is with */}
                        <div className="flex flex-col gap-1">
                            <Typography variant="h5" className="break-words">{threadTitle(selected)}</Typography>
                            <Typography variant="body2" color="secondary" component="div" className="flex flex-wrap items-center gap-x-2">
                                <span>{threadWith(selected)}</span>
                                <span>· {selected.message_count ?? messages.length} message{(selected.message_count ?? messages.length) === 1 ? "" : "s"}</span>
                                {selectedMailbox && multipleMailboxes && <span className="inline-flex items-center gap-2">· <MailboxName mailbox={selectedMailbox} /></span>}
                            </Typography>
                            {!isPersonThread(selected) && (
                                <Typography variant="body2" color="secondary">
                                    Filtered as {filedLabel(selected)}{selected.filed_reason ? ` — ${selected.filed_reason}` : ""}
                                </Typography>
                            )}
                            {isPersonThread(selected) && (selected.prospectId || selected.companyId) && (
                                <div className="flex items-center gap-1 flex-wrap -ml-2">
                                    {selected.prospectId && (
                                        <Button size="small" variant="text" onClick={() => sidePanel.open({ path: "prospects", entityId: selected.prospectId!, selectedTab: "overview" })}>
                                            <LucideIconByName name="UserRound" size={iconSize.smallest} />
                                            {names.get(selected.prospectId) ?? "Open the person"}
                                        </Button>
                                    )}
                                    {selected.companyId && (
                                        <Button size="small" variant="text" onClick={() => sidePanel.open({ path: "companies", entityId: selected.companyId!, selectedTab: "overview" })}>
                                            <LucideIconByName name="Building2" size={iconSize.smallest} />
                                            {names.get(selected.companyId) ?? "Open the company"}
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Actions: one row */}
                        <div className="flex items-center gap-1 flex-wrap -ml-2">
                            {!isPersonThread(selected) && (
                                <Button size="small" variant="text" disabled={working} onClick={() => void setKind(selected, "person")}>
                                    <LucideIconByName name="UserRound" size={iconSize.smallest} /> It is a conversation — back to the inbox
                                </Button>
                            )}
                            {selected.status !== "closed" ? (
                                <Button size="small" variant="text" disabled={working} onClick={() => void patchThread(selected, { status: "closed" }, "Archived — it stays where it was in the mailbox")}>
                                    <LucideIconByName name="Archive" size={iconSize.smallest} /> Archive
                                </Button>
                            ) : (
                                <Button size="small" variant="text" disabled={working} onClick={() => void patchThread(selected, { status: "open" }, "Back in the inbox")}>
                                    <LucideIconByName name="ArchiveRestore" size={iconSize.smallest} /> Unarchive
                                </Button>
                            )}
                            {isPersonThread(selected) && selected.status !== "closed" && (
                                selected.needs_reply !== false ? (
                                    <Button size="small" variant="text" disabled={working} onClick={() => void patchThread(selected, { needs_reply: false }, "Marked as needing no reply")}>
                                        <LucideIconByName name="Check" size={iconSize.smallest} /> No reply needed
                                    </Button>
                                ) : (
                                    <Button size="small" variant="text" disabled={working} onClick={() => void patchThread(selected, { needs_reply: true }, "Back in Waiting on us")}>
                                        <LucideIconByName name="Reply" size={iconSize.smallest} /> Still waiting on us
                                    </Button>
                                )
                            )}
                            {isPersonThread(selected) && selected.status !== "closed" && (
                                <Menu
                                    trigger={
                                        <Button size="small" variant="text" disabled={working}>
                                            <LucideIconByName name="AlarmClock" size={iconSize.smallest} />
                                            {selected.follow_up_at ? `Look again ${followUpLabel(selected, new Date(), true)}` : "Remind me"}
                                        </Button>
                                    }
                                >
                                    <MenuItem dense onClick={() => void remind(selected, 1)}>Tomorrow</MenuItem>
                                    <MenuItem dense onClick={() => void remind(selected, 3)}>In 3 days</MenuItem>
                                    <MenuItem dense onClick={() => void remind(selected, 7)}>In a week</MenuItem>
                                    {selected.follow_up_at && <MenuItem dense onClick={() => void remind(selected, null)}>Clear the reminder</MenuItem>}
                                </Menu>
                            )}
                            {isPersonThread(selected) && (
                                <Button size="small" variant={linking ? "filled" : "text"} onClick={() => setLinking(v => !v)}>
                                    <LucideIconByName name="Link" size={iconSize.smallest} />
                                    {selected.prospectId || selected.companyId ? "Change link" : "Link"}
                                </Button>
                            )}
                            {isPersonThread(selected) ? (
                                <Menu
                                    trigger={
                                        <IconButton size="small" aria-label="More actions">
                                            <LucideIconByName name="Ellipsis" size={iconSize.smallest} />
                                        </IconButton>
                                    }
                                >
                                    <MenuItem dense onClick={() => setSilencing(true)}>
                                        <LucideIconByName name="BellOff" size={iconSize.smallest} /> Silence sender
                                    </MenuItem>
                                    {gmailUrl && (
                                        <MenuItem dense onClick={() => window.open(gmailUrl, "_blank", "noopener,noreferrer")}>
                                            <ExternalLinkIcon size={iconSize.smallest} /> Open in Gmail
                                        </MenuItem>
                                    )}
                                </Menu>
                            ) : gmailUrl && (
                                <Button size="small" variant="text" component="a" href={gmailUrl} target="_blank" rel="noreferrer">
                                    <ExternalLinkIcon size={iconSize.smallest} /> Open in Gmail
                                </Button>
                            )}
                        </div>

                        {silencing && <SilenceCard thread={selected} working={working} onSilence={scope => void setKind(selected, "automated", scope)} onCancel={() => setSilencing(false)} />}

                        {linking && (
                            <ThreadLinkPicker
                                thread={selected}
                                messages={messages}
                                onCancel={() => setLinking(false)}
                                onLinked={async message => {
                                    setLinking(false);
                                    snackbar.open({ type: "success", message });
                                    setNames(new Map());
                                    await reload();
                                    await loadSelected(selected.id);
                                }}
                            />
                        )}

                        {loadingMessages && messages.length === 0
                            ? <CircularProgressCenter />
                            : <EmailThreadView messages={messages} mailboxes={mailboxes} />}

                        {replying ? (
                            <EmailComposer
                                target={{
                                    mode: "reply",
                                    threadId: selected.id,
                                    mailboxId: selected.mailboxId,
                                    to: (selected.participants ?? []).filter(Boolean),
                                    subject: "",
                                    prospectId: selected.prospectId,
                                    companyId: selected.companyId,
                                    recipientName: [...messages].reverse().find(m => m.direction === "inbound")?.from_name ?? linkedName(selected)
                                }}
                                mailboxes={mailboxes}
                                onCancel={() => setReplying(false)}
                                onSent={async () => {
                                    setReplying(false);
                                    snackbar.open({ type: "success", message: "Sent" });
                                    await Promise.all([reload(), loadSelected(selected.id)]);
                                }}
                            />
                        ) : (
                            <div>
                                <Button variant="outlined" onClick={() => setReplying(true)}>
                                    <LucideIconByName name="Reply" size={iconSize.smallest} /> Reply
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * One conversation in the list: who, when, the subject, the first words, and
 * at most a line of state under them. A row, not a card — it takes a fill only
 * when it is hovered or open — and state is a dot before a word, never a pill.
 */
const ThreadRow = React.memo(function ThreadRow({
    thread,
    selected,
    who,
    mailbox,
    onSelect
}: {
    thread: Thread;
    selected: boolean;
    who: string;
    mailbox?: MailboxSummary;
    onSelect: (thread: Thread) => void;
}) {
    const waiting = daysWaiting(thread);
    const followUp = thread.status !== "closed" ? followUpLabel(thread) : null;
    const state: React.ReactNode[] = [];
    if (waiting !== null) {
        state.push(
            <span key="waiting" className="inline-flex items-center gap-1.5">
                <Dot className={waiting >= 7 ? "bg-red-500" : "bg-orange-500"} />
                {waiting === 0 ? "Waiting since today" : `Waiting ${waiting} day${waiting === 1 ? "" : "s"}`}
            </span>
        );
    }
    if (followUp) {
        state.push(
            <span key="follow-up" className="inline-flex items-center gap-1.5">
                {isFollowUpDue(thread) && <Dot className="bg-orange-500" />}
                {followUp.charAt(0).toUpperCase() + followUp.slice(1)}
            </span>
        );
    }
    if (!isPersonThread(thread)) {
        const label = filedLabel(thread);
        state.push(<span key="filed">{label.charAt(0).toUpperCase() + label.slice(1)}</span>);
    }
    if (mailbox) state.push(<MailboxName key="mailbox" mailbox={mailbox} />);

    return (
        <div
            role="button"
            tabIndex={0}
            aria-current={selected || undefined}
            onClick={() => onSelect(thread)}
            onKeyDown={event => {
                if (event.key !== "Enter" && event.key !== " ") return;
                event.preventDefault();
                onSelect(thread);
            }}
            className={cls(
                "rounded-lg px-3 py-2 cursor-pointer outline-none focus-visible:ring-2 focus-visible:ring-primary",
                selected ? "bg-surface-active" : "hover:bg-surface-hover"
            )}
        >
            <div className="flex items-center gap-2">
                <Typography variant="body2" className={cls("truncate grow", thread.unread ? "font-semibold" : "font-medium")}>{who}</Typography>
                {thread.unread && <Dot className="bg-blue-500" />}
                <Typography variant="caption" color="secondary" className="shrink-0">{sinceLabel(thread.last_message_at)}</Typography>
            </div>
            <Typography variant="body2" className={cls("truncate", thread.unread && "font-medium")}>{threadTitle(thread)}</Typography>
            {thread.last_snippet && (
                <Typography variant="caption" color="secondary" className="block truncate">{thread.last_snippet}</Typography>
            )}
            {state.length > 0 && (
                <Typography variant="caption" color="secondary" component="div" className="flex flex-wrap items-center gap-x-3 mt-0.5">
                    {state}
                </Typography>
            )}
        </div>
    );
});

/**
 * Silencing a sender, confirmed. The address, or — for a company's own domain
 * — everything from it. Never a free-mail domain: silencing gmail.com would
 * silence half the people this CRM exists to talk to.
 */
function SilenceCard({
    thread,
    working,
    onSilence,
    onCancel
}: {
    thread: Thread;
    working: boolean;
    onSilence: (scope: "address" | "domain") => void;
    onCancel: () => void;
}) {
    const address = String((thread.participants ?? [])[0] ?? "").toLowerCase();
    const domain = address.split("@")[1] ?? "";
    const corporate = Boolean(domain) && !FREEMAIL.has(domain);
    return (
        <Card className={cls("p-3 flex flex-col gap-2", defaultBorderMixin)}>
            <Typography variant="subtitle2">Silence {address || "this sender"}?</Typography>
            <Typography variant="body2" color="secondary">
                This conversation, and everything they send from now on, goes to Filtered — kept, readable, counted nowhere.
                It can be undone from any of their conversations.
            </Typography>
            <div className="flex gap-2 flex-wrap">
                <Button size="small" variant="outlined" disabled={working || !address} onClick={() => onSilence("address")}>Silence {address}</Button>
                {corporate && <Button size="small" variant="outlined" disabled={working} onClick={() => onSilence("domain")}>Silence everything from @{domain}</Button>}
                <Button size="small" variant="text" disabled={working} onClick={onCancel}>Cancel</Button>
            </div>
        </Card>
    );
}
